// =============================================================================
// auth.ts — Schemas Zod públicos de autenticação.
//
// Reutilizados pelo frontend (React Hook Form) e pelo backend (routes + service).
// Não inclui campos internos (password_hash, refresh_token_hash).
// LGPD: email é PII — coberto por pino.redact na API (doc 17 §8.3).
// =============================================================================
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Login
// ---------------------------------------------------------------------------
export const loginBodySchema = z.object({
    email: z
        .string({ required_error: 'Email é obrigatório' })
        .email('Email inválido')
        .max(255, 'Email muito longo'),
    password: z
        .string({ required_error: 'Senha é obrigatória' })
        .min(1, 'Senha é obrigatória')
        // SEC-05: limite superior de 72 bytes — bcryptjs ignora bytes além de 72,
        // o que abre DoS via entradas longas que bloqueiam o event loop (pré-auth, anônimo).
        .max(72, 'Senha inválida'),
});
export const loginResponseSchema = z.object({
    access_token: z.string(),
    expires_in: z.number().int().positive(),
    /**
     * Primeiro acesso pendente (F56-S01). true = o usuário entrou com a senha
     * temporária e só pode chamar POST /api/auth/first-access (e logout / me) até
     * definir e-mail e senha próprios; qualquer outra rota responde 403
     * CREDENTIALS_CHANGE_REQUIRED. Opcional com default false: um bundle de
     * frontend mais novo que o backend (deploy progressivo) não falha o parse.
     * O backend NUNCA confia neste campo — a trava vive no `authenticate()`.
     */
    must_change_credentials: z.boolean().default(false),
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        full_name: z.string(),
        organization_id: z.string().uuid(),
        /**
         * Permissões RBAC do usuário consolidadas no momento da resposta.
         * Carregadas via JOIN entre `user_roles`, `role_permissions` e `permissions`.
         * O frontend usa para gating de UI (ex.: hub de Configurações).
         * O backend usa `request.user.permissions` carregado pelo middleware `authenticate()`
         * em cada request — a lista aqui é snapshot, não fonte de verdade.
         */
        permissions: z.array(z.string()),
        /**
         * Escopo de cidade do usuário:
         *   null     → admin/gestor_geral — acesso global (sem filtro de cidade).
         *   string[] → UUIDs das cidades permitidas (gestor_regional/agente).
         *   []       → sem cidade configurada (sem acesso a dados de nenhuma cidade).
         *
         * O frontend usa para determinar o scope toggle em /relatorios e evitar
         * mostrar opção "Consolidado/global" para quem não tem acesso global.
         * O backend NUNCA confia neste campo para autorização — usa request.user.cityScopeIds
         * carregado pelo middleware authenticate() via DB.
         */
        city_scope_ids: z.array(z.string().uuid()).nullable(),
        /**
         * Fuso IANA da organização (ex.: "America/Porto_Velho") — F38-S11.
         * Sem gate de permissão: formatar data é necessidade de qualquer papel.
         * O backend sempre populates com o fuso real ou o default documentado
         * (organização sem `timezone` gravado nunca devolve `null` solto).
         * Campo OPCIONAL no Zod: um bundle de frontend mais novo que o backend
         * (durante deploy progressivo) não pode falhar o parse por ausência dele.
         */
        organization_timezone: z.string().min(1).optional(),
    }),
});
// ---------------------------------------------------------------------------
// Refresh
// ---------------------------------------------------------------------------
/** Body vazio — token vem no cookie httpOnly; CSRF no header. */
export const refreshBodySchema = z.object({}).strict();
export const refreshResponseSchema = z.object({
    access_token: z.string(),
    expires_in: z.number().int().positive(),
    /**
     * Mesma semântica de loginResponseSchema.must_change_credentials — ressincronizado
     * a cada refresh, para o SPA saber após um reload se o primeiro acesso ainda
     * está pendente (o access_token vive só em memória).
     */
    must_change_credentials: z.boolean().default(false),
    // user retornado no refresh para rehidratar o store do SPA após reload —
    // o access_token vive só em memória, o cookie persiste no browser.
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        full_name: z.string(),
        organization_id: z.string().uuid(),
        // Permissões RBAC ressincronizadas no refresh — garante que mudanças de role
        // aplicadas após o login original sejam refletidas ao recarregar o SPA.
        permissions: z.array(z.string()),
        /**
         * Escopo de cidade ressincronizado no refresh — reflete mudanças de escopo
         * aplicadas após o login original (ex: gestor_regional recebe nova cidade).
         * Mesma semântica de loginResponseSchema.user.city_scope_ids.
         */
        city_scope_ids: z.array(z.string().uuid()).nullable(),
        /**
         * Fuso IANA da organização — mesma semântica de
         * loginResponseSchema.user.organization_timezone (F38-S11). Ressincronizado
         * a cada refresh, como permissions e city_scope_ids.
         */
        organization_timezone: z.string().min(1).optional(),
    }),
});
// ---------------------------------------------------------------------------
// Logout
// ---------------------------------------------------------------------------
/** Body vazio — sessão identificada pelo cookie refresh. */
export const logoutBodySchema = z.object({}).strict();
// ---------------------------------------------------------------------------
// Primeiro acesso — troca obrigatória de e-mail e senha (F56-S01)
//
// Usuário criado por um administrador entra com senha temporária e fica preso a
// uma única ação: POST /api/auth/first-access. Contrato consumido por F56-S02.
// ---------------------------------------------------------------------------
/**
 * Códigos de erro estáveis do primeiro acesso. Vêm no campo `code` do corpo JSON de erro
 * (`{ statusCode, code, error, message }` — o mesmo campo que `throwFromResponse` de apps/web lê).
 * NÃO renomear sem avisar: o frontend (F56-S02) decide a UX por eles.
 *
 *   CREDENTIALS_CHANGE_REQUIRED  403  qualquer rota fora da allowlist enquanto o primeiro acesso está pendente
 *   FIRST_ACCESS_NOT_PENDING     403  POST /api/auth/first-access com a flag já desligada
 *   INVALID_CURRENT_PASSWORD     422  senha temporária (atual) incorreta (422 e não 401, de propósito)
 *   EMAIL_ALREADY_IN_USE         409  e-mail já usado por outro usuário (em qualquer organização: o login é global)
 *   WEAK_PASSWORD                400  senha nova fora da política (ou igual à atual); `message` lista todas as razões
 *   FST_ERR_VALIDATION           400  e-mail inválido / corpo malformado (validação do Fastify, `message` descreve o campo)
 *
 * INVALID_CURRENT_PASSWORD é 422 justamente para NÃO cair no interceptor de 401 do
 * frontend (refresh + retry + logout): senha temporária errada não é sessão expirada.
 */
export const FIRST_ACCESS_ERROR_CODES = {
    CREDENTIALS_CHANGE_REQUIRED: 'CREDENTIALS_CHANGE_REQUIRED',
    FIRST_ACCESS_NOT_PENDING: 'FIRST_ACCESS_NOT_PENDING',
    INVALID_CURRENT_PASSWORD: 'INVALID_CURRENT_PASSWORD',
    EMAIL_ALREADY_IN_USE: 'EMAIL_ALREADY_IN_USE',
    WEAK_PASSWORD: 'WEAK_PASSWORD',
};
/** Limite superior de bytes relevantes do bcrypt (bcryptjs ignora o que passa de 72). */
export const PASSWORD_MAX_LENGTH = 72;
/**
 * Política da senha nova do primeiro acesso. Espelha `newPasswordSchema`
 * (apps/api/src/modules/account/schemas.ts, usada em POST /api/account/password)
 * com teto de 72 caracteres, o mesmo do login (SEC-05): senha maior que isso
 * nasceria inutilizável.
 *
 * O backend NÃO usa este schema no `body` da rota — aplica no service para
 * devolver o código estável WEAK_PASSWORD em vez de VALIDATION_ERROR genérico.
 * O frontend pode usá-lo para validar antes de enviar.
 */
export const newPasswordPolicySchema = z
    .string()
    .min(8, 'A nova senha deve ter pelo menos 8 caracteres')
    .max(PASSWORD_MAX_LENGTH, `A nova senha deve ter no máximo ${PASSWORD_MAX_LENGTH} caracteres`)
    .regex(/[a-zA-Z]/, 'A nova senha deve conter pelo menos uma letra')
    .regex(/[0-9]/, 'A nova senha deve conter pelo menos um dígito')
    .refine((s) => s === s.trim(), 'A nova senha não pode começar ou terminar com espaço');
/** Corpo de POST /api/auth/first-access. LGPD: email e senhas nunca são logados (pino.redact). */
export const firstAccessBodySchema = z.object({
    /** Senha temporária recebida do administrador — exigida de novo (defesa contra sessão sequestrada). */
    currentPassword: z
        .string({ required_error: 'A senha atual é obrigatória' })
        .min(1, 'A senha atual é obrigatória')
        .max(PASSWORD_MAX_LENGTH, 'Senha inválida'),
    /** E-mail próprio do usuário. Normalizado (trim + minúsculas) antes de validar; único na organização. */
    email: z
        .string({ required_error: 'Email é obrigatório' })
        .trim()
        .toLowerCase()
        .email('Email inválido')
        .max(255, 'Email muito longo'),
    /**
     * Senha nova. Aqui só limita o tamanho (anti-DoS no bcrypt); a política
     * (`newPasswordPolicySchema`) é aplicada pelo service → 400 WEAK_PASSWORD.
     */
    newPassword: z
        .string({ required_error: 'A nova senha é obrigatória' })
        .min(1, 'A nova senha é obrigatória')
        .max(256, 'A nova senha é longa demais'),
});
/**
 * Resposta de sucesso de POST /api/auth/first-access: o MESMO corpo do login
 * (tokens novos, sem o claim de primeiro acesso) + cookies de refresh/CSRF.
 * `must_change_credentials` é sempre false aqui.
 */
export const firstAccessResponseSchema = loginResponseSchema.extend({
    status: z.literal('ok'),
});
/** Resposta de GET /api/auth/me — permite ao SPA saber o estado do primeiro acesso sem outra rota. */
export const authMeResponseSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    full_name: z.string(),
    organization_id: z.string().uuid(),
    must_change_credentials: z.boolean(),
});
//# sourceMappingURL=auth.js.map